import { isDefined, isNonEmpty, isNotDefined, isString } from '@sgme/fp';
import { BatchFormFieldType, formatValue, switchRadioButtons } from 'components/batchForm/BatchForm.helpers';
import {
  areCopyActionKeysPressed,
  doesKeyLooksLikeValidInputForComponent,
  isNumber,
  isSpaceBarKeyPressed,
} from 'components/common-form/builder/form/node/field/Grid/cells/utils';
import {
  decimalRules,
  getSpotAndGreeksEquity,
  getSpotAndGreeksForex,
} from 'components/common-form/builder/form/node/field/SpotAndGreeksField/SpotAndGreeks.helper';
import { displayUnderlyingCode } from 'components/common-form/builder/form/node/field/utils';
import { isIsoDate } from 'components/shared/DatePicker/utils';
import { TenorFieldData } from 'components/shared/TenorField/TenorField';
import { getTimeSpanFormattedValue } from 'components/shared/TimeSpanField/TimeSpanField.helper';
import { Locale } from 'date-fns';
import { AnalyticCategoryActions } from 'models';
import {
  AmountFieldMetadata,
  DateFieldMetadata,
  FieldMetadata,
  LockablePercentFieldData,
  LockablePercentFieldMetadata,
  NumberFieldMetadata,
  PercentFieldMetadata,
  ToggleFieldMetadata,
} from 'models/forms/fields';
import { AvailableItemsFieldBase } from 'models/forms/fields/AvailableItemsField';
import { RadioButtonsFieldMetadata } from 'models/forms/fields/RadioButtonsFieldMetadata';
import { ISalesCounterpart } from 'models/ISalesCounterpart';
import { Underlying } from 'models/underlying';
import { UnderlyingCodeType } from 'models/underlying/base';
import { KeyboardEvent } from 'react';
import { IntlShape } from 'react-intl';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import { GreeksTable } from 'state/quotes/details/details.model';
import { isGridData, parseContentToGridData } from 'utils/clipboard/clipboard-content';
import { roundNumberToPrecision } from 'utils/core/math';
import { getDateOnlyFromIsoString, IsoDateOnly } from 'utils/date';

export async function handleKeyDownOnRadioButtonsField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  isCellEditable: boolean,
  metadata: FieldMetadata,
  isPasting: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  copyToClipboard: (value: string, key: string) => void,
  readPastedContentClipboard: () => Promise<string | string[]>,
  pasteCellId: string,
  setPasteCellId?: (cellId: string) => void,
  setPastedGridData?: (data: Record<string, string>) => void,
): Promise<boolean> {
  const selectionOption = value as string;
  if (isSpaceBarKeyPressed(event) && value !== null && isCellEditable) {
    event.preventDefault();
    const radioButtonsFieldMetadata: RadioButtonsFieldMetadata = metadata as RadioButtonsFieldMetadata;
    switchRadioButtons(radioButtonsFieldMetadata.availableItems, selectionOption, setValue);
    return true;
  }
  if (areCopyActionKeysPressed(event)) {
    copyToClipboard(selectionOption, event.key);
    return true;
  }
  if (isPasting) {
    const pastedSelectedOption = (await readPastedContentClipboard()) as string;

    const parsedData = parseContentToGridData(pastedSelectedOption, pasteCellId);

    if (isGridData(parsedData) && setPasteCellId && setPastedGridData) {
      setPasteCellId(pasteCellId);
      setPastedGridData(parsedData);
      return true;
    }

    const { availableItems }: RadioButtonsFieldMetadata = metadata as RadioButtonsFieldMetadata;
    if (isDefined(availableItems.find(item => item.value === pastedSelectedOption))) {
      setValue(pastedSelectedOption);
      return true;
    }
  }
  return false;
}

export async function handleKeyDownOnToggleField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  isCellEditable: boolean,
  isPasting: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  copyToClipboard: (value: string, key: string) => void,
  readPastedContentClipboard: () => Promise<string | string[]>,
  metadata: ToggleFieldMetadata,
  pasteCellId: string,
  setPasteCellId?: (cellId: string) => void,
  setPastedGridData?: (data: Record<string, string>) => void,
): Promise<boolean> {
  if (isSpaceBarKeyPressed(event) && isCellEditable && value !== null) {
    event.preventDefault();
    setValue(!value);
    return true;
  }
  if (areCopyActionKeysPressed(event)) {
    const toggleValue = value as boolean;
    copyToClipboard(toggleValue.toString(), event.key);
    return true;
  }
  if (isPasting) {
    const pastedToggleValue = (await readPastedContentClipboard()) as string;

    const parsedData = parseContentToGridData(pastedToggleValue, pasteCellId);
    if (isGridData(parsedData) && setPasteCellId && setPastedGridData) {
      setPasteCellId(pasteCellId);
      setPastedGridData(parsedData);
      return true;
    }

    const parsedToggleValue = formatValue<boolean>(pastedToggleValue as string, metadata.type);
    if (parsedToggleValue === value) {
      return true;
    }
    setValue(parsedToggleValue);
    return true;
  }
  return false;
}

export async function handleKeyDownOnListField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  metadata: AvailableItemsFieldBase,
  isPasting: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  copyToClipboard: (value: string, key: string) => void,
  readPastedContentClipboard: () => Promise<string | string[]>,
  pasteCellId: string,
  isCellEditable: boolean,
  setPasteCellId?: (cellId: string) => void,
  setPastedGridData?: (data: Record<string, string>) => void,
): Promise<boolean> {
  if (isSpaceBarKeyPressed(event) && isCellEditable) {
    return true;
  }
  if (areCopyActionKeysPressed(event)) {
    copyToClipboard(value as string, event.key);
    return true;
  }
  if (isPasting && isCellEditable) {
    const pastedSelectedItem = (await readPastedContentClipboard()) as string;

    const parsedData = parseContentToGridData(pastedSelectedItem, pasteCellId);

    if (isGridData(parsedData) && setPasteCellId && setPastedGridData) {
      setPasteCellId(pasteCellId);
      setPastedGridData(parsedData);
      return true;
    }

    const { availableItems } = metadata;

    if (isDefined(availableItems.find(item => item.value === pastedSelectedItem))) {
      setValue(pastedSelectedItem);
      return true;
    }
  }
  return false;
}

export function handleKeyDownOnLockableField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  canEdit: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  setIsEditing: ((isEditing: boolean) => void) | undefined,
  setIsSelectAll: ((isSelectAll: boolean) => void) | undefined,
  setLostInputBeforeEditing: (lostInput: string | undefined) => void,
  matomoCategory: keyof AnalyticCategoryActions,
  metadata: LockablePercentFieldMetadata,
): boolean {
  if (isSpaceBarKeyPressed(event) && canEdit) {
    if (isNotDefined(value) && setIsEditing && setIsSelectAll) {
      logAnalytics.trackEvent(matomoCategory, 'Edit field (Space)');
      setValue({ value: 0, customized: true } satisfies LockablePercentFieldData);
      setIsSelectAll(true);
      setIsEditing(true);
    } else {
      setValue(null);
    }
    return true;
  }
  if (doesKeyLooksLikeValidInputForComponent(event.key, metadata.type) && canEdit && setIsEditing) {
    if (isNumber(event.key)) {
      logAnalytics.trackEvent(matomoCategory, 'Edit numeric field (Numeric key)');
    }
    setValue({ value: 0, customized: true } satisfies LockablePercentFieldData);
    setLostInputBeforeEditing(event.key);
    setIsEditing(true);
    return true;
  }

  return false;
}

export async function handleKeyDownOnUnderlyingsField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  isPasting: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  setIsEditing: ((isEditing: boolean) => void) | undefined,
  copyToClipboard: (value: string, key: string) => void,
  readPastedContentClipboard: () => Promise<string | string[]>,
  setLostInputBeforeEditing: (lostInput: string | undefined) => void,
  matomoCategory: keyof AnalyticCategoryActions,
  isDeleteKeyPressed: (_e: KeyboardEvent<Element>) => boolean,
  canEdit: boolean,
  setPastedData: ((data: string | undefined) => void) | undefined,
): Promise<boolean> {
  const underlyings = value as Underlying[];
  if (isDeleteKeyPressed(event) && canEdit) {
    if (underlyings.length !== 0) {
      logAnalytics.trackEvent(matomoCategory, 'Clear field (Delete)');
      setValue([] as Underlying[]);
      setLostInputBeforeEditing(undefined);
    }
    return true;
  }
  if (areCopyActionKeysPressed(event)) {
    copyToClipboard(underlyings.map(u => u.id).join(','), event.key);
    return true;
  }
  if (isPasting && setIsEditing && canEdit && setPastedData) {
    const pastedUnderlyingIds = await readPastedContentClipboard();
    setPastedData(pastedUnderlyingIds as string);
    setIsEditing(true);
    return true;
  }
  return false;
}

export async function handleKeyDownOnClientListField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  isPasting: boolean,
  lostInputBeforeEditing: string | undefined,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  setIsEditing: ((isEditing: boolean) => void) | undefined,
  copyToClipboard: (value: string, key: string) => void,
  readPastedContentClipboard: () => Promise<string | string[]>,
  setLostInputBeforeEditing: (lostInput: string | undefined) => void,
  matomoCategory: keyof AnalyticCategoryActions,
  isDeleteKeyPressed: (_e: KeyboardEvent<Element>) => boolean,
  canEdit: boolean,
): Promise<boolean> {
  if (isDeleteKeyPressed(event) && isDefined(value) && canEdit) {
    logAnalytics.trackEvent(matomoCategory, 'Clear field (Delete)');
    setValue(null);
    setLostInputBeforeEditing(undefined);
    return true;
  }
  const client = isDefined(value) ? (value as ISalesCounterpart) : undefined;
  if (areCopyActionKeysPressed(event) && isDefined(client)) {
    copyToClipboard(client?.id, event.key);
    return true;
  }
  if (isPasting && canEdit) {
    const pastedClientId = (await readPastedContentClipboard()) as string;

    if (
      isNonEmpty(pastedClientId) &&
      isString(pastedClientId) &&
      lostInputBeforeEditing !== pastedClientId &&
      setIsEditing
    ) {
      setLostInputBeforeEditing(pastedClientId);
      setIsEditing(true);
      return true;
    }
  }
  return false;
}

export async function handleKeyDownOnDateField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  isPasting: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  copyToClipboard: (value: string, key: string) => void,
  readPastedContentClipboard: () => Promise<string | string[]>,
  { type }: DateFieldMetadata,
  pasteCellId: string,
  canEdit: boolean,
  setPasteCellId?: (cellId: string) => void,
  setPastedGridData?: (data: Record<string, string>) => void,
): Promise<boolean> {
  const date = getDateOnlyFromIsoString(value as string);
  if (areCopyActionKeysPressed(event) && isDefined(date)) {
    copyToClipboard(date, event.key);
    return true;
  }
  if (isPasting && canEdit) {
    const pastedDate = (await readPastedContentClipboard()) as string;

    const parsedData = parseContentToGridData(pastedDate, pasteCellId);
    if (isGridData(parsedData) && setPasteCellId && setPastedGridData) {
      setPasteCellId(pasteCellId);
      setPastedGridData(parsedData);
      return true;
    }

    const newDateValue = formatValue<IsoDateOnly>(pastedDate, type) as string;
    if (isNonEmpty(newDateValue) && isIsoDate(newDateValue as string)) {
      setValue(newDateValue as string);
      return true;
    }
  }
  return false;
}

export async function handleKeyDownOnNumericField(
  event: KeyboardEvent,
  metadata: FieldMetadata,
  value: Readonly<BatchFormFieldType | undefined>,
  isPasting: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  copyToClipboard: (value: string, key: string) => void,
  readPastedContentClipboard: () => Promise<string | string[]>,
  pasteCellId: string,
  setPasteCellId?: (cellId: string) => void,
  setPastedGridData?: (data: Record<string, string>) => void,
): Promise<boolean> {
  const numericValue = value as number;
  if (areCopyActionKeysPressed(event) && isDefined(numericValue)) {
    copyToClipboard(numericValue.toString(), event.key);
    return true;
  }
  if (isPasting) {
    const pastedNumber = await readPastedContentClipboard();

    const parsedData = parseContentToGridData(pastedNumber as string, pasteCellId);
    if (isGridData(parsedData) && setPasteCellId && setPastedGridData) {
      setPasteCellId(pasteCellId);
      setPastedGridData(parsedData);
      return true;
    }

    if (isNonEmpty(pastedNumber) && isString(pastedNumber)) {
      const parsedNumericValue = parseFloat(pastedNumber);
      const { nbDecimals } = metadata as AmountFieldMetadata | NumberFieldMetadata | PercentFieldMetadata;
      const roundedNumericValue = roundNumberToPrecision(parsedNumericValue, nbDecimals ?? 0);
      setValue(roundedNumericValue);
      return true;
    }
  }
  return false;
}

export async function handleKeyDownOnTenorField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  isPasting: boolean,
  setValue: (value: Readonly<BatchFormFieldType | undefined>) => void,
  copyToClipboard: (value: string, key: string) => void,
  readPastedContentClipboard: () => Promise<string | string[]>,
  pasteCellId: string,
  setPasteCellId?: (cellId: string) => void,
  setPastedGridData?: (data: Record<string, string>) => void,
): Promise<boolean> {
  const tenorData = value as TenorFieldData;
  if (areCopyActionKeysPressed(event) && isDefined(tenorData.currentValue.value)) {
    const tenorValue = tenorData.currentValue.value;
    copyToClipboard(tenorValue.toString(), event.key);
    return true;
  }
  if (isPasting) {
    const pastedTenorValue = await readPastedContentClipboard();

    const parsedData = parseContentToGridData(pastedTenorValue as string, pasteCellId);
    if (isGridData(parsedData) && setPasteCellId && setPastedGridData) {
      setPasteCellId(pasteCellId);
      setPastedGridData(parsedData);
      return true;
    }

    if (isNonEmpty(pastedTenorValue) && isString(pastedTenorValue)) {
      const parsedTenorValue = parseFloat(pastedTenorValue);
      const roundedTenorValue = roundNumberToPrecision(parsedTenorValue, 0);
      const newTenorValue: TenorFieldData = {
        ...tenorData,
        currentValue: { ...tenorData.currentValue, value: roundedTenorValue },
      };
      setValue(newTenorValue);
      return true;
    }
  }
  return false;
}

export function handleKeyDownOnTextField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  copyToClipboard: (value: string, key: string) => void,
): boolean {
  if (areCopyActionKeysPressed(event) && isDefined(value)) {
    copyToClipboard(value as string, event.key);
    return true;
  }
  return false;
}

export function handleKeyDownOnTimeSpanField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  copyToClipboard: (value: string, key: string) => void,
  locale: Locale,
): boolean {
  if (areCopyActionKeysPressed(event) && isDefined(value)) {
    copyToClipboard(getTimeSpanFormattedValue(value as string, locale), event.key);
    return true;
  }
  return false;
}

export function handleKeyDownOnVerticalSpotAndGreeksField(
  event: KeyboardEvent,
  value: Readonly<BatchFormFieldType | undefined>,
  underlyings: readonly Underlying[],
  copyToClipboard: (value: string, key: string) => void,
  intl: IntlShape,
  defaultUnderlyingCodeType?: UnderlyingCodeType,
): void {
  if (areCopyActionKeysPressed(event) && isDefined(value)) {
    const highlighted = window.getSelection();
    const highlightedString = highlighted?.toString();
    if (highlightedString === '') {
      const SpotAndGreeksString = getSpotAndGreeksString(
        value as GreeksTable,
        underlyings,
        intl,
        defaultUnderlyingCodeType,
      );
      copyToClipboard(SpotAndGreeksString, event.key);
    } else {
      copyToClipboard(highlightedString as string, event.key);
    }
  }
}

function getSpotAndGreeksString(
  value: GreeksTable,
  underlyings: readonly Underlying[],
  intl: IntlShape,
  defaultUnderlyingCodeType?: UnderlyingCodeType,
): string {
  return value?.assetClass === 'Forex'
    ? getSpotAndGreeksForexString(underlyings, value, intl)
    : getSpotAndGreeksEquityString(underlyings, value, intl, defaultUnderlyingCodeType);
}

function getSpotAndGreeksForexString(underlyings: readonly Underlying[], value: GreeksTable, intl: IntlShape): string {
  let spotAndGreeksString = '';
  const spotAndGreeks = getSpotAndGreeksForex(value, underlyings);
  spotAndGreeks.forEach(spotAndGreek => {
    if (spotAndGreek.spot !== undefined) {
      spotAndGreeksString += 'Spot';
      spotAndGreeksString += ' ';
      spotAndGreeksString += spotAndGreek?.underlying?.id;
      spotAndGreeksString += ' ';
      spotAndGreeksString += intl.formatNumber(spotAndGreek.spot, {
        minimumFractionDigits: decimalRules.Forex.spot.minimumFractionDigits,
        maximumFractionDigits: decimalRules.Forex.spot.maximumFractionDigits,
      });
    }

    if (spotAndGreek.fxDelta !== undefined) {
      spotAndGreeksString += '\n';
      spotAndGreeksString += spotAndGreek.fxDelta.reference?.replace('(%)', '');
      spotAndGreeksString += intl.formatNumber(spotAndGreek.fxDelta.value, {
        minimumFractionDigits: decimalRules.Forex.fxDelta.minimumFractionDigits,
        maximumFractionDigits: decimalRules.Forex.fxDelta.maximumFractionDigits,
      });
      spotAndGreeksString += '%';
    }
    if (spotAndGreek.fxVega !== undefined) {
      spotAndGreeksString += '\n';
      spotAndGreeksString += spotAndGreek.fxVega.reference?.replace('(%)', '');
      spotAndGreeksString += intl.formatNumber(spotAndGreek.fxVega.value, {
        minimumFractionDigits: decimalRules.Forex.fxVega.minimumFractionDigits,
        maximumFractionDigits: decimalRules.Forex.fxVega.maximumFractionDigits,
      });
      spotAndGreeksString += '%';
    }
  });
  return spotAndGreeksString;
}

function getSpotAndGreeksEquityString(
  underlyings: readonly Underlying[],
  value: GreeksTable,
  intl: IntlShape,
  defaultUnderlyingCodeType: UnderlyingCodeType = 'Bloomberg',
): string {
  let spotAndGreeksString = '';
  const spotAndGreeks = getSpotAndGreeksEquity(value, underlyings);
  spotAndGreeks.forEach((spotAndGreek, i) => {
    if (i !== 0) {
      spotAndGreeksString += '\n\n';
    }
    spotAndGreeksString += displayUnderlyingCode(defaultUnderlyingCodeType, spotAndGreek.underlying);
    if (spotAndGreek.spot !== undefined) {
      spotAndGreeksString += '\n';
      spotAndGreeksString += intl.formatMessage({ id: 'form.SpotAndGreeks.Spot' });
      spotAndGreeksString += ' ';
      spotAndGreeksString += intl.formatNumber(spotAndGreek.spot, {
        minimumFractionDigits: decimalRules.Equity.spot.minimumFractionDigits,
        maximumFractionDigits: decimalRules.Equity.spot.maximumFractionDigits,
      });
    }
    if (spotAndGreek.deltaPct !== undefined) {
      spotAndGreeksString += '\n';
      spotAndGreeksString += intl.formatMessage({ id: 'form.SpotAndGreeks.Delta' });
      spotAndGreeksString += ' ';
      spotAndGreeksString += intl.formatNumber(spotAndGreek.deltaPct, {
        minimumFractionDigits: decimalRules.Equity.deltaPct.minimumFractionDigits,
        maximumFractionDigits: decimalRules.Equity.deltaPct.maximumFractionDigits,
      });
      spotAndGreeksString += '%';
    }
    if (spotAndGreek.deltaInShare !== undefined) {
      spotAndGreeksString += '\n';
      spotAndGreeksString += intl.formatMessage({ id: 'form.SpotAndGreeks.DeltaShares' });
      spotAndGreeksString += ' ';
      spotAndGreeksString += intl.formatNumber(spotAndGreek.deltaInShare, {
        minimumFractionDigits: decimalRules.Equity.deltaInShare.minimumFractionDigits,
        maximumFractionDigits: decimalRules.Equity.deltaInShare.maximumFractionDigits,
      });
    }
    if (spotAndGreek.vega !== undefined) {
      spotAndGreeksString += '\n';
      spotAndGreeksString += intl.formatMessage({ id: 'form.SpotAndGreeks.Vega' });
      spotAndGreeksString += ' ';
      spotAndGreeksString += intl.formatNumber(spotAndGreek.vega, {
        minimumFractionDigits: decimalRules.Equity.vega.minimumFractionDigits,
        maximumFractionDigits: decimalRules.Equity.vega.maximumFractionDigits,
      });
      spotAndGreeksString += '%';
    }
  });

  return spotAndGreeksString;
}
